import * as React from 'react'
import { ReactElement, useContext, useMemo } from 'react'
import { Button, makeStyles, Text, tokens, Tooltip } from '@fluentui/react-components'
import { Link } from 'react-router-dom'
import { ToastContentProps } from 'react-toastify'
import { bundleIcon, DismissSquareFilled, EditOffFilled, EditOffRegular, OpenFilled, OpenRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import useAxios from '../../hooks/useAxios'
import { useSettings } from '../../hooks/useSettings'
import { useTimeDivisorSetting } from '../../hooks/file/useTimeDivisorSetting'
import { QueueData, QueueStatus } from '../../hooks/useQueueStateHandler'
import FormattedDateTime from '../Date/FormattedDateTime'
import { TimeFormats } from '../../helpers/dateHelper'
import { mapOverviewItemToTrackTimeSuggestionDto, TimeSuggestionOverviewDto, TimeSuggestionStatus } from '../../pages/Time/BaseTimeSuggestionOverview'
import PopupContext from '../../context/PopupContext'
import useUser from '../../hooks/useUser'
import { AxiosInstance } from 'axios'
import { TrackTimeSuggestionDto } from '../../v2/components/Dialog/Registration/TimeSuggestionMutationDialogBody'

const DismissSquareIcon = bundleIcon(DismissSquareFilled, DismissSquareFilled)
const DontTrackIcon = bundleIcon(EditOffFilled, EditOffRegular)
const OpenIcon = bundleIcon(OpenFilled, OpenRegular)

const useStyles = makeStyles({
    toaster: {
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'auto',
        gap: tokens.spacingHorizontalXS,
    },
    icon: {
        alignSelf: 'center',
        textAlign: 'center',
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '1fr 100px',
        gap: tokens.spacingHorizontalXS,
    },
    buttonRow: {
        minHeight: '32px',
        display: 'grid',
        gridTemplateColumns: '1fr',
        marginTop: tokens.spacingVerticalS,
        gap: tokens.spacingHorizontalS,
    },
    rowSingle: {
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
        paddingTop: tokens.spacingHorizontalS,
        gap: tokens.spacingHorizontalM,
    },
    alignEnd: {
        textAlign: 'end',
    },
    colorRed: {
        color: tokens.colorStatusDangerForeground3,
    },
})

export function onClickDontTrackTime(
    axios: AxiosInstance,
    values: Partial<TrackTimeSuggestionDto>,
    onDismiss: () => void,
    onError: (error: any) => void,
) {
    return () =>
        axios
            .put(
                `/time-suggestion/${values.timeSuggestionId}`,
                { status: TimeSuggestionStatus.NoTracking },
                {
                    headers: { SuppressErrors: 'true' },
                },
            )
            .then(() => {
                onDismiss?.()
            })
            .catch(error => onError(error))
}

export default function TimeSuggestionToastContent(props: ToastContentProps<QueueData<object, TimeSuggestionOverviewDto, object>>): ReactElement {
    const axios = useAxios()
    const { i18n, t: translate } = useTranslation()
    const { settings } = useSettings()
    const popupContext = useContext(PopupContext)
    const styles = useStyles()
    const timeDivisor = useTimeDivisorSetting()
    const { currentUser } = useUser()

    const timeMutation = useMemo<Partial<TrackTimeSuggestionDto>>(
        () =>
            props.data.status?.file?.id
                ? mapOverviewItemToTrackTimeSuggestionDto(
                      props.data.status,
                      timeDivisor,
                      i18n.language,
                      settings.timeSuggestionDescription,
                      currentUser?.id,
                  )
                : undefined,
        [props.data.status],
    )

    if ([QueueStatus.Dispatch, QueueStatus.Queued, QueueStatus.Delayed].includes(props.data.status.current) || !timeMutation || !props.data.status) {
        return <div>{translate('base.loading')}</div>
    }
    if ([QueueStatus.Failed].includes(props.data.status.current)) {
        return <div>Fout...</div>
    }

    return (
        <div className={styles.toaster}>
            <div>
                <Text size={400} weight={'semibold'}>
                    {translate('time.suggestion.title')}
                </Text>
            </div>
            <div className={styles.row}>
                <Text>{timeMutation.emailInbound ? props.data.status.senderName : props.data.status.recipientsName || ''}</Text>
                <FormattedDateTime date={props.data.status.emailSent} format={TimeFormats.ISO} className={styles.alignEnd} />
            </div>
            <div className={styles.row}>
                <Text>{props.data.status.subject || ''}</Text>
                <Tooltip relationship={'label'} content={props.data.status.file.name} withArrow>
                    <Link key={props.data.status.id} to={`/file/${props.data.status.file.id}`} className={styles.alignEnd}>
                        <Text size={200}>{props.data.status.file.reference}</Text>
                    </Link>
                </Tooltip>
            </div>
            <div className={styles.rowSingle}>
                <Tooltip content={translate('time.suggestion.cancel.tooltip')} relationship={'label'}>
                    <Button icon={<DismissSquareIcon />} className={styles.colorRed} appearance={'transparent'} onClick={props.closeToast} />
                </Tooltip>
                <Tooltip content={translate('time.suggestion.donttrack.tooltip')} relationship={'label'}>
                    <Button
                        icon={<DontTrackIcon />}
                        appearance={'outline'}
                        onClick={() =>
                            onClickDontTrackTime(
                                axios,
                                timeMutation,
                                () => props.closeToast(),
                                error => {
                                    console.log(error)
                                },
                            )()
                        }
                    />
                </Tooltip>
                <Tooltip content={translate('time.suggestion.open.tooltip')} relationship={'label'}>
                    <Button
                        icon={<OpenIcon />}
                        appearance={'primary'}
                        onClick={() => {
                            popupContext.setShowTimeTrackingDialog(timeMutation)
                            props.closeToast()
                        }}
                    />
                </Tooltip>
            </div>
        </div>
    )
}
